import { Box, Button, Tooltip } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArticleContent } from '../models/Article';
import AssetImage from './AssetImage';
import { Palette } from 'Palette';
import { StyledHeading, StyledTypography } from './styles/styles';

interface ArticleCardProps {
  content: ArticleContent;
  linkTo?: string;
  linkText?: string;
  tone?: keyof typeof Palette;
}

// Utility function to extract title, content, and image from ArticleContent
const extractContentData = (content: ArticleContent) => {
  // Try to get title and content from headline first
  const headlineTitle = content.headline?.properties?.heading?.content;
  const headlineContent = content.headline?.properties?.content;

  // Try to get title from imageCarousel first item
  const carouselTitle = content.imageCarousel?.properties?.items?.[0]?.heading?.content;
  const carouselText = content.imageCarousel?.properties?.items?.[0]?.text?.content;

  // Try to get image from imageCarousel first item
  const imageUrl = content.imageCarousel?.properties?.items?.[0]?.imageUrl?.url;

  // Extract content text from headline or carousel
  let contentText = '';
  if (headlineContent) {
    // Handle different content structures
    if (typeof headlineContent === 'string') {
      contentText = headlineContent;
    } else if (Array.isArray(headlineContent)) {
      // If it's an array, join the content strings
      contentText = headlineContent
        .map((item) => (typeof item === 'string' ? item : (item as any).content || ''))
        .join(' ');
    } else if (headlineContent.content) {
      contentText = headlineContent.content;
    }
  } else if (carouselText) {
    contentText = carouselText;
  }

  return {
    title: headlineTitle || carouselTitle || 'Untitled Article',
    content: contentText,
    imageUrl: imageUrl || '',
  };
};

// Custom hook to detect if text is overflowing
const useTextOverflow = (text: string) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsOverflowing(element.scrollHeight > element.clientHeight);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text]);

  return { textRef, isOverflowing };
};

const ArticleCard: React.FC<ArticleCardProps> = ({
  content: articleContent,
  linkTo,
  linkText = 'Read',
  tone = 'tone1',
}) => {
  const colors = Palette[tone];
  const navigate = useNavigate();
  const { title, content, imageUrl } = extractContentData(articleContent);
  const { textRef, isOverflowing } = useTextOverflow(title);

  const handleReadClick = () => {
    if (linkTo) {
      navigate(linkTo);
    }
  };

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: Palette.tone4.background,
            color: Palette.tone4.text,
            '& .MuiTooltip-arrow': {
              color: Palette.tone4.background,
            },
          },
        },
      }}
      title={
        isOverflowing ? (
          <Box
            sx={{
              maxWidth: '300px',
              display: 'inline-flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <StyledTypography
              tone="tone4"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}
            >
              {title}
            </StyledTypography>
            {content && typeof content === 'string' && (
              <StyledTypography
                tone="tone4"
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {content.length > 200 ? `${content.substring(0, 200)}...` : content}
              </StyledTypography>
            )}
          </Box>
        ) : (
          ''
        )
      }
      arrow
      placement="top"
      disableHoverListener={!isOverflowing}
    >
      <Box
        className="article-card"
        sx={{
          backgroundColor: Palette.tone2.background,
          color: colors.text,
          borderRadius: '0.625rem',
          overflow: 'hidden',
          cursor: 'pointer',
          width: { xs: '100%', lg: '360px' },
          maxWidth: { xs: '100%', lg: '360px' },
          maxHeight: '6.875rem', // 110px max height
          display: 'flex',
          flexDirection: 'row',
          padding: '0',
          gap: '0',
          alignItems: 'flex-start',
          position: 'relative',
          boxSizing: 'border-box',
          // Responsive width for mobile devices (md down = < 960px)
          '@media (max-width: 960px)': {
            width: '100%',
            maxWidth: '100%',
          },
        }}
      >
        {/* Left side - Text and Button */}
        <Box
          className="article-card-text"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: '100px',
            rowGap: '0.625rem',
            paddingTop: '0.625rem', // 10px top padding
            paddingBottom: '0.625rem', // 10px bottom padding
            paddingLeft: '1rem',
            paddingRight: '0.5rem',
            marginRight: '150px',
            boxSizing: 'border-box',
            // Reduce image space on mobile devices
            '@media (max-width: 960px)': {
              marginRight: '120px',
            },
            '@media (max-width: 480px)': {
              marginRight: '100px',
            },
            '& >.MuiTypography-body1': {
              maxHeight: '1.875rem', // Reduce for 2 lines
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2, // Change to 2 lines
              WebkitBoxOrient: 'vertical',
              paddingBottom: '0.625rem',
            },
          }}
        >
          <StyledHeading
            ref={textRef}
            headerVariant="subheading"
            reduceBottomPadding
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2, // Change to 2 lines
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.3rem',
              maxHeight: '1.8rem',
              textAlign: 'left',
              cursor: isOverflowing ? 'help' : 'default',

              color: '#2c5530',
              fontSize: '1rem',
            }}
          >
            {title}
          </StyledHeading>

          <Button
            variant="outlined"
            onClick={handleReadClick}
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
            sx={{
              borderColor: '#2c5530',
              color: '#2c5530',
              borderRadius: '1rem',
              textTransform: 'none',
              fontSize: '0.875rem',
              padding: '0.5rem 1rem',
              alignSelf: 'flex-start',
              minWidth: '5.625rem', // 90px width
              width: '5.625rem', // 90px width
              height: '1.875rem', // 30px height
              '&:hover': {
                borderColor: '#1a3a1e',
                backgroundColor: 'rgba(44, 85, 48, 0.04)',
              },
            }}
          >
            {linkText}
          </Button>
        </Box>

        {/* Right side - Image */}
        <AssetImage
          url={imageUrl}
          alt={title}
          sx={{
            width: 150,
            height: '100%',
            objectFit: 'cover',
            borderRadius: '0',
            flexShrink: 0,
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            // Responsive image sizing for mobile
            '@media (max-width: 960px)': {
              width: 120,
            },
            '@media (max-width: 480px)': {
              width: 100,
            },
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default ArticleCard;
